import { useEffect } from "react";
import { TopBar, DestinationRow } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";

/**
 * DevelopmentChrome — issue #940 (D175 M2/P1-P5), the `/development` chrome
 * slice. Wired in by `pages/DevelopmentPlan.jsx`, which is the player's
 * page alone (D175 P4 — this component never
 * mounts for a coach or an admin, so it carries no role check of its own,
 * same reasoning `PlanScreen.jsx`'s own header gives for the same gate one
 * level up).
 *
 * Copies `PlanScreen.jsx`'s own `useChromelessOverride` mount contract
 * (`setActive(true)` on mount, `setActive(false)` on unmount) — but, UNLIKE
 * every navy-`TopBar` screen this component otherwise resembles, does NOT
 * call `useRetireTextSize()`.
 *
 * PR #957 review round 3, F2 (an orchestrator correction, not a builder
 * defect) — round 2's own ruling ("#797 retires text size on every
 * rebuilt screen") over-read #797: D25 (decisions-2026-09-24.md:48-58)
 * retires the control only where a MEASURED check shows a screen's text
 * does NOT move at a larger size — never by "this screen was redrawn"
 * alone. The reviewer measured `/development?tab=reflections` on the
 * `all` leg: at Large, the tab body's own H2 moves 25.5px -> 28.5px and
 * its lede paragraph 17px -> 19px (`Reflections.jsx`'s content is REM,
 * not this kit's fixed-px ramp — the ORIGINAL, correct reasoning for
 * keeping the control here). So `/development` keeps the text-size
 * control on BOTH legs — never retired, flag on or off.
 *
 * PR #957 review round 4 — round 3 read `hideTextSize` as one lever with
 * no way to keep the D160/#930 navy-bar visuals without ALSO dropping
 * text size, so it accepted the shipped white "Aa" circle here as the
 * necessary cost. Wrong: both requirements hold at once (D25 keeps text
 * size; the drawing and F5 still want the compact navy control), and
 * `AccessibilityMenu.jsx` now has the lever that keeps them apart —
 * `compact` gives the trigger's own visuals (32px tap area, the inset
 * ring, the white contrast glyph) independently of `hideTextSize`, which
 * this file never passes. `AccessibilityMenu compact` here: the drawn
 * trigger, a full popover (text size AND high contrast), "Accessibility
 * options" as its aria-label — every existing `hideTextSize` caller is
 * untouched (see that file's own doc comment on `compact`).
 *
 * D175 P1 — this renders the EXISTING, unmodified `DestinationRow`
 * (`current="plan"`, no `onCurrentClick`) — D92's three chips
 * (Record/Plan/Grounds), never `Reflect.dc.html`'s own five-chip row
 * (which also draws the missing `nav-performance`/`nav-learn` and
 * "Matches"/"Training"): that row is pre-D92 and explicitly out of scope
 * for this slice (D175 P1) — the conformance table's own covered findings.
 * `kit/DestinationRow.jsx` itself is untouched by this issue.
 *
 * The header block (eyebrow + 22px title, Reflect.dc.html's chrome,
 * `data-dc-id="reflections-page"`'s own header rows) and `children` (the
 * tab strip `DevelopmentPlan.jsx` still owns entirely) both sit inside one
 * `var(--nw-space-page)`-padded stack, 14px apart — the drawing's own
 * `padding: 20px` / `gap: 14px` for this block.
 *
 * #1164: two optional props. `sections` is a node (the Plan section strip)
 * rendered first in that column, above the header; `title` is the header's
 * title ("Your plan" by default, the current section's name on Plan). With
 * neither, the output is what it was.
 */
export function DevelopmentChrome({ children, sections = null, title = "Your plan" }) {
  const { setActive: setChromeless } = useChromelessOverride();

  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="white"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="dev-topbar"
      />
      <DestinationRow current="plan" />

      <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
        {/* #1164 (D233.5): the section strip sits directly under the ribbon, above the heading. */}
        {sections}
        <div className="flex flex-col" style={{ gap: 4 }}>
          <div className="nw-eyebrow">Your plan</div>
          {/* `.nw-title` is 24px (index.css) — Reflect.dc.html's own chrome
              header draws this title at 22px, so the size is set explicitly
              here rather than reused from that class. */}
          <div
            data-testid="plan-title"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: "-0.5px",
              lineHeight: 1.15,
              color: "var(--nw-ink)",
            }}
          >
            {title}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default DevelopmentChrome;
